import { Container, DisplayObject, Point, Sprite } from "pixi.js"

import type { GlintSubject } from "./dropped_item_glint"

/**
 * A picked-up item flying into whoever picked it up.
 *
 * It used to vanish off the ground while a separate "+1" icon rose off the picker's head, so the eye
 * had to jump from one to the other and nothing ever showed the item reaching anybody. Now the item's
 * OWN art leaves the spot it lay on and goes into the picker's chest, and the player's running count
 * (PickupTally) goes up as it lands (EntityRenderingManager.itemPickedUp).
 *
 * TWO BEATS, about a quarter of a second together:
 *   PLUCK -- it hops a few pixels up off the ground and swells a little, easing out. Being picked up.
 *   PULL  -- it accelerates into the chest along a curve that bows upward, shrinking and coming
 *            upright (drops lie at random angles), and lands in a small warm flash
 *            (TransientLights "pickup").
 * Short, and following the item's own path, so on the hundredth pickup it still reads as a thing
 * being gathered rather than as a notification.
 *
 * IN THE WORLD SURFACE, beside the bodies, not in the overlay: it is a thing in the world, so night
 * darkens it as it darkened the item, and it sorts like one -- at the item's own depth while it lifts
 * (a drop behind the picker's heels stays behind them) and just in front of the picker once it is
 * being pulled in.
 *
 * THE TARGET IS READ OFF THE PICKER'S RENDERABLE EVERY FRAME. Pickup is walk-over, so the picker is
 * nearly always moving, and a target fixed at launch lands the item behind them.
 *
 * A heap picked up in one step arrives as a quick stream, STAGGER_MS apart, rather than as one lump.
 *
 * Driven off the render loop (EntityRenderingManager.endFrame) and timed off the clock, never a
 * timer: a backgrounded tab that comes back finds its flights already over.
 *
 * LIVE TUNING, from the browser console -- no reload:
 *   window.pickupFlight = false             off: items vanish and are counted at once
 *   window.pickupFlight = { pullMs: 260 }   override any key of PICKUP_FLIGHT
 */

export interface PickupFlightTuning {
    /** The pluck: how long, how high (world px) and how much it swells. */
    liftMs: number
    liftPx: number
    pop: number
    /** The pull into the chest: how long, and how hard it accelerates (1 = constant speed). */
    pullMs: number
    pullPower: number
    /** How far the path bows up above the plucked point, world px. */
    arcPx: number
    /** Its size as it reaches the chest, against the size it lay at. */
    endScale: number
    /** Between the items of a heap picked up together, and the most any one is held back. */
    staggerMs: number
    maxDelayMs: number
}

export const PICKUP_FLIGHT: PickupFlightTuning = {
    liftMs: 90,
    liftPx: 6,
    pop: 1.15,
    pullMs: 170,
    pullPower: 2.2,
    arcPx: 10,
    endScale: 0.4,
    staggerMs: 60,
    maxDelayMs: 360,
}

// Where the item goes in: the upper chest, where an armful of something is held.
//
// An Ember body says where its chest is (LpcComposite.pickupChest): CHEST_BELOW_HEAD under the head
// joint of the frame on show, so it suits every build and rides the walk. Measured on the default
// build standing: head joint y=-24.5 in the body's space, hands +4.5, soles +28 -- the body's origin
// is at the BELLY, not the shoulders an older LPC measurement put it at.
export const CHEST_BELOW_HEAD = 14
// A body with no rig to ask (an LPC sheet) gets this, in its own space: on a 64px LPC frame the
// shoulders sit just above the origin.
export const PICKER_CHEST_Y = 0

/** A picker's renderable, which may know where its own chest is (in its own space). */
export interface PickupBody extends DisplayObject {
    pickupChest?: () => { x: number; y: number } | undefined
}

// A heap after a wave is the worst case; past this the oldest flight is landed on the spot.
const MAX_FLIGHTS = 24

/** The item as it lies on the ground, read off its renderable at the moment it is picked up. */
export interface PickupItemArt {
    /** The container the item was drawn in, and where in it. */
    layer: Container
    x: number
    y: number
    zIndex: number
    /** The item's sprite and the angle it lies at. */
    sprite: Sprite
    rotation: number
}

/** A dropped item's renderable as a flight's starting point; undefined if it has no art or no layer. */
export const pickupArtOf = (
    item: GlintSubject & { zIndex: number },
    layer: Container | null = item.parent as Container | null,
): PickupItemArt | undefined => {
    const art = item.glintArt()
    if (!art || !layer) return undefined
    return { layer, x: item.x, y: item.y, zIndex: item.zIndex, sprite: art.sprite, rotation: art.rotation }
}

/** The picker's renderable, resolved every frame; undefined once it has gone. */
export type PickupPicker = () => PickupBody | undefined

interface Flight {
    flyer: Container
    turned: Container
    startMs: number
    fromX: number
    fromY: number
    restZ: number
    rotation: number
    picker: PickupPicker
    onLanded?: () => void
    toX: number
    toY: number
}

const easeOut = (t: number) => 1 - (1 - t) * (1 - t)
const smoothstep = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t))

const tuning = (): PickupFlightTuning | undefined => {
    const live = typeof window === "undefined" ? undefined : (window as any).pickupFlight
    if (live === false) return undefined
    return live && typeof live === "object" ? { ...PICKUP_FLIGHT, ...live } : PICKUP_FLIGHT
}

export class PickupFlights {
    private flights: Flight[] = []
    private lastStartMs = -Infinity
    private roomId: string | undefined
    private readonly chest = new Point(0, PICKER_CHEST_Y)
    private readonly target = new Point()

    /** `flash` lights the landing; x/y are in `layer`, the container the flight was drawn in. */
    constructor(private flash: (x: number, y: number, layer: Container) => void = () => undefined) {}

    /**
     * Send `item` into the picker. False when it cannot fly (switched off, or the picker has no place
     * in the world), in which case nothing has been drawn and the caller carries on without it.
     */
    launch = (item: PickupItemArt, picker: PickupPicker, onLanded?: () => void, nowMs: number = performance.now()): boolean => {
        const tune = tuning()
        const body = picker()
        const layer = body?.parent as Container | null | undefined
        if (!tune || !body || !layer) return false
        if (this.flights.length >= MAX_FLIGHTS) this.land(0, false)

        // The art's middle inside the item's turned container: zero for an atlas item (its frame
        // carries a centred anchor), off to one side for a hand-placed `tileset:id` sprite. The flyer
        // pivots there, so it shrinks into its own centre rather than towards a corner.
        const model = item.sprite
        const w = model.texture.width * model.scale.x
        const h = model.texture.height * model.scale.y
        const mx = model.position.x + (0.5 - model.anchor.x) * w
        const my = model.position.y + (0.5 - model.anchor.y) * h
        const cos = Math.cos(item.rotation)
        const sin = Math.sin(item.rotation)
        let from = new Point(item.x + cos * mx - sin * my, item.y + sin * mx + cos * my)
        if (item.layer !== layer) from = layer.toLocal(from, item.layer)

        const sprite = new Sprite(model.texture)
        sprite.anchor.copyFrom(model.anchor)
        sprite.scale.copyFrom(model.scale)
        sprite.tint = model.tint
        sprite.position.set(model.position.x - mx, model.position.y - my)
        const turned = new Container()
        turned.rotation = item.rotation
        turned.addChild(sprite)
        const flyer = new Container()
        flyer.interactiveChildren = false
        flyer.position.copyFrom(from)
        flyer.zIndex = item.zIndex
        flyer.addChild(turned)
        layer.addChild(flyer)

        const startMs = Math.min(Math.max(nowMs, this.lastStartMs + tune.staggerMs), nowMs + tune.maxDelayMs)
        this.lastStartMs = startMs
        this.flights.push({
            flyer, turned, startMs, fromX: from.x, fromY: from.y, restZ: item.zIndex, rotation: item.rotation,
            picker, onLanded, toX: from.x, toY: from.y,
        })
        return true
    }

    update = (roomId: string | undefined, nowMs: number = performance.now()) => {
        if (roomId !== this.roomId) {
            if (this.roomId !== undefined) this.clear()
            this.roomId = roomId
        }
        if (this.flights.length < 1) return
        const tune = tuning() ?? PICKUP_FLIGHT
        for (let i = this.flights.length - 1; i >= 0; i--) {
            const flight = this.flights[i]
            const { flyer } = flight
            if (flyer.destroyed || !flyer.parent) {
                this.land(i, false)
                continue
            }
            const age = nowMs - flight.startMs
            if (age < 0) continue // held back behind the rest of its heap, still lying where it was

            const body = flight.picker()
            if (!body?.parent) {
                this.land(i, false)
                continue
            }
            const chest = body.pickupChest?.()
            this.chest.set(chest?.x ?? 0, chest?.y ?? PICKER_CHEST_Y)
            const target = flyer.parent.toLocal(this.chest, body, this.target)
            flight.toX = target.x
            flight.toY = target.y

            if (age < tune.liftMs) {
                const p = easeOut(age / tune.liftMs)
                flyer.position.set(flight.fromX, flight.fromY - tune.liftPx * p)
                flyer.scale.set(1 + (tune.pop - 1) * p)
                flight.turned.rotation = flight.rotation * (1 - 0.5 * p)
                flyer.alpha = 1
                flyer.zIndex = flight.restZ
                continue
            }
            const v = (age - tune.liftMs) / tune.pullMs
            if (v >= 1) {
                this.land(i)
                continue
            }
            // A quadratic curve from the plucked point to the chest, its control point straight above
            // the plucked point: an item lying beside the picker goes up first and then across, the
            // way a thing is lifted into the arms, and one lying at their feet comes straight up.
            const e = Math.pow(v, tune.pullPower)
            const x0 = flight.fromX
            const y0 = flight.fromY - tune.liftPx
            const cx = x0
            const cy = y0 - tune.arcPx
            const a = (1 - e) * (1 - e)
            const b = 2 * (1 - e) * e
            const c = e * e
            flyer.position.set(a * x0 + b * cx + c * target.x, a * y0 + b * cy + c * target.y)
            flyer.scale.set(tune.pop + (tune.endScale - tune.pop) * e)
            flight.turned.rotation = flight.rotation * 0.5 * (1 - e)
            flyer.alpha = 1 - 0.5 * smoothstep((e - 0.55) / 0.45)
            flyer.zIndex = body.zIndex + 0.5
        }
    }

    /** Drop every flight where it is, unlit: a room change. */
    clear = () => {
        for (const flight of this.flights) {
            if (!flight.flyer.destroyed) flight.flyer.destroy({ children: true })
            flight.onLanded?.()
        }
        this.flights = []
        this.lastStartMs = -Infinity
    }

    // Exposed for tests.
    liveCount = (): number => this.flights.length

    private land = (i: number, lit: boolean = true) => {
        const [flight] = this.flights.splice(i, 1)
        const layer = flight.flyer.parent as Container | null
        if (lit && layer) this.flash(flight.toX, flight.toY, layer)
        if (!flight.flyer.destroyed) flight.flyer.destroy({ children: true })
        flight.onLanded?.()
    }
}
